// Library methods
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

// MUI Components
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Radio from "@mui/material/Radio";
import SearchBox from "../../../components/UI/SearchBox";
import Alert from "../../../components/UI/Alert";
import { MultiplePagesIcon, SinglePageIcon } from "../../../assets/icons";

// Utils
import useClinic from "../../../hooks/useClinic";

const ReportSettings = ({ currentSettings, handleUpdateSettings }) => {
  // internationalization
  const { t } = useTranslation();

  // clinic context
  const { clinicLicensing } = useClinic();
  const canUseExamPackaging = clinicLicensing?.canUseExamPackaging;

  const monocularReportPdfLayoutOption = useMemo(
    () => ({
      ...currentSettings?.monocularReportPdfLayoutOption,
      name: t(
        `settings_monocular_${currentSettings?.monocularReportPdfLayoutOption?.val?.toLowerCase()}`
      ),
    }),
    [t, currentSettings?.monocularReportPdfLayoutOption]
  );

  const reportDisplayLeftEyeFirst = useMemo(
    () => Boolean(currentSettings?.reportDisplayLeftEyeFirst),
    [currentSettings?.reportDisplayLeftEyeFirst]
  );

  const dataArray = useMemo(
    () => [
      { name: t("settings_monocular_single"), val: "Single" },
      { name: t("settings_monocular_multiple"), val: "Multiple" },
    ],
    [t]
  );

  const handleSetState = useCallback(
    (value) => {
      // only update the pdf layout if:
      // the value is different than the current setting
      // the value is a valid type that exists in dataArray
      if (
        value?.val &&
        value?.val?.toLowerCase() !==
          monocularReportPdfLayoutOption?.val?.toLowerCase() &&
        dataArray.find(
          (data) => data?.val?.toLowerCase() === value?.val?.toLowerCase()
        )
      ) {
        handleUpdateSettings({
          ...currentSettings,
          monocularReportPdfLayoutOption: value,
        });
      }
    },
    [
      monocularReportPdfLayoutOption,
      dataArray,
      currentSettings,
      handleUpdateSettings,
    ]
  );

  const handleUpdateRadio = useCallback(
    (input) => {
      handleUpdateSettings({
        ...currentSettings,
        reportDisplayLeftEyeFirst: input,
      });
    },
    [handleUpdateSettings, currentSettings]
  );

  return (
    <Grid container display="flex" width="100%" columnGap="32px" rowGap="24px">
      <Box
        width="100%"
        padding="24px"
        borderRadius="16px"
        backgroundColor="#FFF"
        boxShadow="0px 4px 10px rgba(0, 0, 0, 0.15)"
      >
        <Box display="flex" flexDirection="column" gap="20px">
          <Box display="flex" flexDirection="column" gap="8px">
            <Typography fontSize={{ xs: "18px", sm: "20px" }} fontWeight={600}>
              {t("settings_monocular_padf_layout")}
            </Typography>
            {canUseExamPackaging ? (
              <Alert
                variant="warning"
                alertContent={`${t("monocular_report_alert_2")} ${t("monocular_report_alert")}`}
                mb="8px"
              />
            ) : (
              <Alert
                variant="info"
                alertContent={t("monocular_report_alert_2")}
                mb="8px"
              />
            )}

            <Typography fontSize="14px" color="#5B6D7A">
              {t("monocular_report_description")}
            </Typography>
            <Typography fontSize="14px" color="#5B6D7A">
              {t("monocular_report_description_2")}
            </Typography>
            <Box display="flex" justifyContent="space-around" mb="12px">
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap="8px"
              >
                <SinglePageIcon color="#5B6D7A" />
                <Typography fontSize="14px" color="#5B6D7A">
                  {t("settings_monocular_single")}
                </Typography>
              </Box>

              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap="8px"
              >
                <MultiplePagesIcon color="#5B6D7A" />
                <Typography fontSize="14px" color="#5B6D7A">
                  {t("settings_monocular_multiple")}
                </Typography>
              </Box>
            </Box>
            <Box>
              <SearchBox
                data={dataArray}
                label={t("settings_monocular_layout")}
                state={monocularReportPdfLayoutOption}
                setState={handleSetState}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        width="100%"
        padding="24px"
        borderRadius="16px"
        backgroundColor="#FFF"
        boxShadow="0px 4px 10px rgba(0, 0, 0, 0.15)"
      >
        <Box display="flex" flexDirection="column" gap="8px">
          <Typography fontSize={{ xs: "18px", sm: "20px" }} fontWeight={600}>
            {t("report_eye_display_order")}
          </Typography>
          <Typography fontSize="14px" color="#5B6D7A">
            {t("report_eye_display_content")}
          </Typography>
          <Grid>
            <Grid item display="flex" alignItems="center">
              <Radio
                checked={reportDisplayLeftEyeFirst}
                onChange={() => handleUpdateRadio(true)}
                name="radio-buttons"
                inputProps={{ "aria-label": "Left" }}
                sx={{
                  "&.MuiButtonBase-root.MuiRadio-root": {
                    color: "#E2772E",
                  },
                }}
              />
              <Typography fontSize="14px" color="#5B6D7A">
                {t("left_eye_display_order")}
              </Typography>
            </Grid>
            <Grid item display="flex" alignItems="center">
              <Radio
                checked={!reportDisplayLeftEyeFirst}
                onChange={() => handleUpdateRadio(false)}
                name="radio-buttons"
                inputProps={{ "aria-label": "Right" }}
                sx={{
                  "&.MuiButtonBase-root.MuiRadio-root": {
                    color: "#E2772E",
                  },
                }}
              />
              <Typography fontSize="14px" color="#5B6D7A">
                {t("right_eye_display_order")}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Grid>
  );
};

export default ReportSettings;
